import {
    type CSSProperties,
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    type MarkdownVariantsType,
    type IMarkdownEditorProps,
    MarkdownEditor
} from "ncore-ui-kit";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";
import ncoreLogo from "../../assets/ncore-logo-preview.png";

type EditorMode = NonNullable<IMarkdownEditorProps["mode"]>;

const NAME = "MarkdownEditor";

const USAGE_CODE = `const [value, setValue] = useState("# Release notes");

<MarkdownEditor
    onChangeText={setValue}
    value={value}
/>`;

const MODES_CODE = `const [mode, setMode] = useState<"live" | "write" | "preview">("live");

<MarkdownEditor
    onModeChange={setMode}
    initialValue={text}
    mode={mode}
/>`;

const VARIANTS_CODE = `<MarkdownEditor
    variants={{
        "# ": "titleMediumSize",
        "## ": "titleSmallSize",
        "<p>": "bodyMediumSize"
    }}
    initialValue={text}
/>`;

const IMAGES_CODE = `const handleImageInsert = async () => {
    const file = await pickImage();

    if(!file) {
        return null;
    }

    const url = await uploadImage(file);

    return {
        alt: file.name,
        url
    };
};

<MarkdownEditor
    onImageInsert={handleImageInsert}
    initialValue={text}
/>`;

const EMPTY_MARKDOWN = " ";

const FULL_WIDTH_STYLE: CSSProperties = {
    width: "100%"
};

const COMPACT_VARIANTS: MarkdownVariantsType = {
    "# ": "titleMediumSize",
    "## ": "titleSmallSize",
    "<p>": "bodyMediumSize"
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("markdownEditorPage.modesTitle"),
        id: "modes"
    },
    {
        title: t("markdownEditorPage.formattingTitle"),
        id: "formatting"
    },
    {
        title: t("markdownEditorPage.imagesTitle"),
        id: "images"
    },
    {
        title: t("markdownEditorPage.appearanceTitle"),
        id: "appearance"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const FormattingDemo = () => {
    const {
        t
    } = useDocsI18n();

    const [
        value,
        setValue
    ] = useState(() => t("markdownEditorPage.formatSample"));

    return <>
        <Preview
            caption={t("markdownEditorPage.formattingCaption")}
        >
            <div
                style={FULL_WIDTH_STYLE}
            >
                <MarkdownEditor
                    onChangeText={setValue}
                    defaultMode="write"
                    value={value}
                />
            </div>
        </Preview>
        <CodeBlock
            code={value || EMPTY_MARKDOWN}
            language="markdown"
        />
    </>;
};

const ImagesDemo = () => {
    const {
        t
    } = useDocsI18n();

    const [
        value,
        setValue
    ] = useState(() => t("markdownEditorPage.imagesSample"));

    const handleImageInsert = () => {
        return {
            alt: t("markdownEditorPage.imagesAlt"),
            url: ncoreLogo
        };
    };

    return <>
        <Preview
            caption={t("markdownEditorPage.imagesCaption")}
        >
            <div
                style={FULL_WIDTH_STYLE}
            >
                <MarkdownEditor
                    onImageInsert={handleImageInsert}
                    onChangeText={setValue}
                    value={value}
                />
            </div>
        </Preview>
        <CodeBlock
            code={IMAGES_CODE}
            language="tsx"
        />
    </>;
};

const MarkdownEditorPage = () => {
    const {
        language,
        t
    } = useDocsI18n();

    const [
        mode,
        setMode
    ] = useState<EditorMode>("live");

    const sample = t("markdownEditorPage.sample");

    return <Article>
        <ComponentHeader
            lead={t("markdownEditorPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("markdownEditorPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("markdownEditorPage.overviewCaption")}
            >
                <div
                    style={FULL_WIDTH_STYLE}
                >
                    <MarkdownEditor
                        initialValue={sample}
                        key={language}
                    />
                </div>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("markdownEditorPage.modesTitle")}
            id="modes"
        >
            <Paragraph>
                {t("markdownEditorPage.modesText")}
            </Paragraph>
            <Preview
                caption={t("markdownEditorPage.modesCaption", [
                    mode
                ])}
            >
                <div
                    style={FULL_WIDTH_STYLE}
                >
                    <MarkdownEditor
                        onModeChange={setMode}
                        initialValue={sample}
                        key={language}
                        mode={mode}
                    />
                </div>
            </Preview>
            <CodeBlock
                code={MODES_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("markdownEditorPage.formattingTitle")}
            id="formatting"
        >
            <Paragraph>
                {t("markdownEditorPage.formattingText")}
            </Paragraph>
            <FormattingDemo
                key={language}
            />
        </Section>
        <Section
            title={t("markdownEditorPage.imagesTitle")}
            id="images"
        >
            <Paragraph>
                {t("markdownEditorPage.imagesText")}
            </Paragraph>
            <ImagesDemo
                key={language}
            />
        </Section>
        <Section
            title={t("markdownEditorPage.appearanceTitle")}
            id="appearance"
        >
            <Paragraph>
                {t("markdownEditorPage.appearanceText")}
            </Paragraph>
            <Preview
                caption={t("markdownEditorPage.appearanceCaption")}
            >
                <div
                    style={FULL_WIDTH_STYLE}
                >
                    <MarkdownEditor
                        variants={COMPACT_VARIANTS}
                        initialValue={sample}
                        key={language}
                    />
                </div>
            </Preview>
            <CodeBlock
                code={VARIANTS_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("markdownEditorPage.statesText")}
            </Paragraph>
            <Preview
                caption={t("markdownEditorPage.statesCaption")}
            >
                <div
                    style={FULL_WIDTH_STYLE}
                >
                    <MarkdownEditor
                        initialValue={sample}
                        editable={false}
                        key={language}
                    />
                </div>
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("markdownEditorPage.a11yInput"),
                    t("markdownEditorPage.a11yModes"),
                    t("markdownEditorPage.a11yToolbar"),
                    t("markdownEditorPage.a11yShortcuts"),
                    t("markdownEditorPage.a11yTouch"),
                    t("markdownEditorPage.a11yStatus")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("markdownEditorPage.propInitialValue"),
                        defaultValue: "\"\"",
                        name: "initialValue",
                        type: "string"
                    },
                    {
                        description: t("markdownEditorPage.propValue"),
                        name: "value",
                        type: "string"
                    },
                    {
                        description: t("markdownEditorPage.propOnChangeText"),
                        type: "(text: string) => void",
                        name: "onChangeText"
                    },
                    {
                        description: t("markdownEditorPage.propDefaultMode"),
                        type: "\"live\" | \"write\" | \"preview\"",
                        defaultValue: "\"live\"",
                        name: "defaultMode"
                    },
                    {
                        description: t("markdownEditorPage.propMode"),
                        type: "\"live\" | \"write\" | \"preview\"",
                        name: "mode"
                    },
                    {
                        type: "(mode: \"live\" | \"write\" | \"preview\") => void",
                        description: t("markdownEditorPage.propOnModeChange"),
                        name: "onModeChange"
                    },
                    {
                        description: t("markdownEditorPage.propSyntax"),
                        type: "\"standard\" | \"legacy\"",
                        defaultValue: "\"standard\"",
                        name: "syntax"
                    },
                    {
                        type: "Partial<Record<MarkdownKeys, keyof Typography>>",
                        description: t("markdownEditorPage.propVariants"),
                        name: "variants"
                    },
                    {
                        description: t("markdownEditorPage.propEditable"),
                        defaultValue: "true",
                        name: "editable",
                        type: "boolean"
                    },
                    {
                        description: t("markdownEditorPage.propSpreadBehaviour"),
                        type: "\"baseline\" | \"stretch\" | \"free\"",
                        defaultValue: "\"stretch\"",
                        name: "spreadBehaviour"
                    },
                    {
                        type: "() => { url: string; alt?: string } | Promise<{ url: string; alt?: string } | null | undefined>",
                        description: t("markdownEditorPage.propOnImageInsert"),
                        name: "onImageInsert"
                    },
                    {
                        description: t("markdownEditorPage.propIsEditorResizable"),
                        name: "isEditorResizable",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("markdownEditorPage.propIsViewerResizable"),
                        name: "isViewerResizable",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("markdownEditorPage.propEditorStyle"),
                        type: "StyleProp<TextStyle>",
                        name: "editorStyle"
                    },
                    {
                        description: t("markdownEditorPage.propStyle"),
                        type: "StyleProp<ViewStyle>",
                        name: "style"
                    },
                    {
                        description: t("markdownEditorPage.propToolbarContainerStyle"),
                        name: "toolbarContainerStyle",
                        type: "StyleProp<ViewStyle>"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("markdownEditorPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("markdownEditorPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("markdownEditorPage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default MarkdownEditorPage;
